import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Prohibit, LockOpen } from "@phosphor-icons/react";
import { toast } from "@/lib/toast";
import type { Client } from "@/types";

async function patchBlock(clientId: string, body: { blocked: boolean; reason?: string }) {
  const res = await fetch(`/api/clients/${clientId}/block`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const data = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(data?.error || "Greška pri izmjeni blokade.");
  }
  return res.json();
}

export function ClientBlockControls({
  client,
  onDone,
}: {
  client: Client;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState("");
  const isBlocked = !!client.blockedAt;

  const mutation = useMutation({
    mutationFn: (body: { blocked: boolean; reason?: string }) => patchBlock(client.id, body),
    onSuccess: (_data, vars) => {
      toast.success(
        vars.blocked
          ? "Klijent je blokiran i obaviješten emailom."
          : "Klijent je deblokiran i obaviješten emailom.",
      );
      queryClient.invalidateQueries({ queryKey: ["admin", "clients"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "stats"] });
      onDone();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  if (isBlocked) {
    return (
      <Button
        variant="outline"
        onClick={() => mutation.mutate({ blocked: false })}
        disabled={mutation.isPending}
      >
        <LockOpen data-icon="inline-start" />
        {mutation.isPending ? "Deblokiranje..." : "Deblokiraj klijenta"}
      </Button>
    );
  }

  if (!confirming) {
    return (
      <Button
        variant="outline"
        className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
        onClick={() => setConfirming(true)}
      >
        <Prohibit data-icon="inline-start" />
        Blokiraj klijenta
      </Button>
    );
  }

  return (
    <div className="flex w-full flex-col gap-2 rounded-xl border border-rose-200 bg-rose-50/50 p-3">
      <p className="text-xs text-muted-foreground">
        Blokada otkazuje sve buduće zakazane seanse klijenta i šalje mu obavijest emailom. Razlog
        ostaje interni i ne prikazuje se klijentu.
      </p>
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Razlog blokade (obavezno, interno)"
        maxLength={1000}
        rows={2}
        className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-rose-500/30"
      />
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={() => setConfirming(false)} disabled={mutation.isPending}>
          Odustani
        </Button>
        <Button
          size="sm"
          className="bg-rose-600 text-white hover:bg-rose-700"
          disabled={!reason.trim() || mutation.isPending}
          onClick={() => mutation.mutate({ blocked: true, reason: reason.trim() })}
        >
          {mutation.isPending ? "Blokiranje..." : "Potvrdi blokadu"}
        </Button>
      </div>
    </div>
  );
}
